/**
 * TenantThemeAdmin — tenant-admin / Enterprise-tier surface for publishing
 * white-label theme overrides. Restricted to color.accent.* values + brand
 * asset URLs; foundation tokens stay locked.
 *
 * Per Design Brief §11 Phase 4: every product decision needs a "partner-
 * ready?" check. This is the surface partners use to make their tenant
 * theirs.
 */

import { useEffect, useState } from "react";
import { useSession } from "../lib/useSession.js";
import {
  SectionHeader, Callout, InThisBlueprint, FrictionCounterMove,
  HedgeMark,
} from "../components/index.js";

const ACCENT_KEYS = [
  { path: "color.accent.navy",            label: "Primary navy",            preview: "Brand primary, borders, badges" },
  { path: "color.accent.navy-dark",       label: "Navy (darker)",            preview: "Hover / active states" },
  { path: "color.accent.steel",           label: "Steel blue",               preview: "Tip callout · counter-move accent" },
  { path: "color.accent.steel-dark",      label: "Steel (darker)",           preview: "" },
  { path: "color.accent.powder",          label: "Powder blue",              preview: "Remember callout" },
  { path: "color.accent.coral",           label: "Coral / warm anchor",      preview: "Watch Out callout · friction · key insight detail" },
  { path: "color.accent.coral-dark",      label: "Coral (darker)",           preview: "" },
  { path: "color.accent.coral-very-soft", label: "Coral (very soft tint)",   preview: "Friction panel background" },
  { path: "color.accent.pale-soft",       label: "Pale (soft)",              preview: "Counter-move panel · pale-cyan tint" },
];

export default function TenantThemeAdminPage() {
  const { session, appUser, loading } = useSession();
  const [overrides, setOverrides] = useState({});
  const [brandAssets, setBrandAssets] = useState({
    logo_light_url: "",
    logo_dark_url: "",
    logo_mark_url: "",
    blueprint_export_header_url: "",
  });
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedPayload, setSavedPayload] = useState(null);

  useEffect(() => {
    if (!session) return;
    fetch("/.netlify/functions/tenant-theme", {
      headers: { authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => r.json())
      .then((b) => {
        if (b?.overrides) setOverrides(b.overrides);
        setBrandAssets({
          logo_light_url: b?.logo_light_url || "",
          logo_dark_url: b?.logo_dark_url || "",
          logo_mark_url: b?.logo_mark_url || "",
          blueprint_export_header_url: b?.blueprint_export_header_url || "",
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setFetching(false));
  }, [session]);

  const setOverride = (path, value) => {
    setOverrides((o) => {
      const next = { ...o };
      if (!value || value === "") delete next[path];
      else next[path] = value;
      return next;
    });
  };

  const publish = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/tenant-theme", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          overrides,
          ...brandAssets,
        }),
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || "Publish failed");
      else setSavedPayload(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || fetching) return <p style={{ padding: 24 }}>Loading…</p>;
  if (!session) return <p style={{ padding: 24 }}>Sign in to manage tenant theming.</p>;
  if (!appUser?.is_tenant_admin) {
    return (
      <div style={{ maxWidth: 540, margin: "60px auto", padding: "0 24px" }}>
        <h1 style={titleStyle}>Tenant admin access required</h1>
        <p style={paragraphStyle}>
          Theme publishing is restricted to tenant admins on the Enterprise tier. Contact your tenant admin or upgrade your tier to manage white-label theming.
        </p>
      </div>
    );
  }

  // Live-preview style overrides (CSS variable names from the override paths)
  const previewVars = Object.fromEntries(
    Object.entries(overrides).map(([path, val]) => [
      "--ppr-" + path.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase().replace(/\./g, "-"),
      val,
    ])
  );

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px" }}>
      <SectionHeader index={1} title="Tenant theming" />

      <Callout.Tip>
        Override the locked brand colors for your tenant. Foundation tokens (typography, spacing, motion, callout structure, semantic mappings) stay tenant-locked per the Phase 1 RE-LOCK.
      </Callout.Tip>

      {error && <ErrorBox error={error} />}

      {savedPayload && (
        <Callout.Tip>
          <strong>Theme published.</strong> Overrides applied: {Object.keys(savedPayload.overrides).length}.
          {savedPayload.rejected_paths?.length ? ` Rejected non-permitted paths: ${savedPayload.rejected_paths.join(", ")}.` : ""}
        </Callout.Tip>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>
        <div>
          <SectionHeader index={2} title="Accent palette" />
          <div style={{ display: "grid", gap: 12 }}>
            {ACCENT_KEYS.map((k) => (
              <ColorRow
                key={k.path}
                path={k.path}
                label={k.label}
                preview={k.preview}
                value={overrides[k.path] || ""}
                onChange={(v) => setOverride(k.path, v)}
              />
            ))}
          </div>

          <SectionHeader index={3} title="Brand assets" />
          <div style={{ display: "grid", gap: 12 }}>
            {[
              ["logo_light_url",                "Logo (for light surfaces)"],
              ["logo_dark_url",                 "Logo (for dark surfaces)"],
              ["logo_mark_url",                 "Logo mark / favicon"],
              ["blueprint_export_header_url",   "Blueprint export header"],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  type="url"
                  value={brandAssets[key]}
                  onChange={(e) => setBrandAssets((b) => ({ ...b, [key]: e.target.value }))}
                  placeholder="https://…"
                  style={inputStyle}
                />
              </Field>
            ))}
          </div>

          <button onClick={publish} disabled={saving} style={{ ...primaryBtn, marginTop: 20 }}>
            {saving ? "Publishing…" : "Publish theme"}
          </button>
        </div>

        <div>
          <SectionHeader index="∗" title="Live preview" />
          <Callout.Remember>
            What you see below re-skins live as you edit. Foundation tokens stay locked — your color picks affect borders, badges, callouts, and accents only.
          </Callout.Remember>

          <div style={{ ...previewVars, marginTop: 12 }} data-component="ThemePreview">
            <InThisBlueprint
              items={[
                <span key="1"><strong>Reading the room</strong> — what tends to land</span>,
                <span key="2">The opening that <HedgeMark>probably</HedgeMark> disarms</span>,
              ]}
            />

            <Callout.Tip>
              Brand color flips. <strong>Borders, badges, callouts, and the In-This-Blueprint top stripe re-skin together.</strong>
            </Callout.Tip>

            <Callout.WatchOut>
              Watch Out callout — coral becomes your warm anchor.
            </Callout.WatchOut>

            <Callout.KeyInsight>
              Key Insight — navy + coral cross-palette pairing.
            </Callout.KeyInsight>

            <FrictionCounterMove
              friction={<>Generic example friction text — coral-tinted panel.</>}
              counterMove={<>Generic example counter-move — pale-cyan tinted.</>}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorRow({ path, label, preview, value, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, alignItems: "center" }}>
      <div>
        <div style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: "13px", color: "var(--ppr-color-accent-navy)" }}>{label}</div>
        <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "10px", color: "var(--ppr-color-text-muted)" }}>{path}</div>
        {preview && (
          <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "11px", color: "var(--ppr-color-text-secondary)", marginTop: 2 }}>{preview}</div>
        )}
      </div>
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        style={{ width: 36, height: 36, border: "1px solid var(--ppr-color-border-default)", padding: 0, cursor: "pointer" }}
      />
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="default"
        style={{
          width: 100,
          padding: "8px 10px",
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "11px",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-background-surface)",
          outline: "none",
        }}
      />
    </div>
  );
}

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-3xl)",
  color: "var(--ppr-color-accent-navy)",
  margin: "16px 0 12px",
};
const paragraphStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  color: "var(--ppr-color-text-secondary)",
};
const inputStyle = {
  padding: "10px 12px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
};
const primaryBtn = {
  padding: "12px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
      marginBottom: 16,
    }}>
      {error}
    </div>
  );
}
